/**
 * Deal Create Modal — form for creating a new deal.
 */

import { useState } from 'react'
import { useCRM } from '@/hooks/useCRM'
import { useDefaultCurrency } from '@/hooks/useDefaultCurrency'
import type { Pipeline } from '@/types/crm'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  useToast,
} from '@/components/ui'

interface DealCreateModalProps {
  pipeline: Pipeline
  onClose: () => void
  defaultStageId?: string
  initialContactId?: string
}

const NO_SOURCE = 'unspecified'

export function DealCreateModal({ pipeline, onClose, defaultStageId, initialContactId }: DealCreateModalProps) {
  const { createDeal } = useCRM()
  const { addToast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const defaultCurrency = useDefaultCurrency()

  // Find first non-won, non-lost stage as default
  const initialStage = defaultStageId
    || pipeline.stages.find((s) => !s.isWon && !s.isLost)?.id
    || pipeline.stages[0]?.id

  const [formData, setFormData] = useState({
    name: '',
    stageId: initialStage || '',
    amountCents: '',
    expectedCloseDate: '',
    source: '',
    notes: '',
  })

  const handleSubmit = async () => {
    if (!formData.name.trim()) return

    setIsSubmitting(true)
    const result = await createDeal({
      name: formData.name.trim(),
      pipelineId: pipeline.id,
      stageId: formData.stageId,
      amountCents: formData.amountCents ? Math.round(parseFloat(formData.amountCents) * 100) : undefined,
      expectedCloseDate: formData.expectedCloseDate || undefined,
      source: formData.source || undefined,
      notes: formData.notes || undefined,
      ...(initialContactId ? { contactId: initialContactId } : {}),
    })
    setIsSubmitting(false)
    if (result) {
      addToast({ type: 'success', title: 'Deal created' })
      onClose()
    } else {
      addToast({ type: 'error', title: 'Failed to create deal', description: 'Check your connection and try again.' })
    }
  }

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose() }}>
      <ModalContent
        size="sm"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <ModalHeader><ModalTitle>New Deal</ModalTitle></ModalHeader>
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit() }}>
        <ModalBody className="space-y-3">
          <Input
            label="Deal Name"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Wedding Cake for Maria"
            autoFocus
          />

          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Stage</label>
            <Select
              value={formData.stageId}
              onValueChange={(v) => setFormData({ ...formData, stageId: v })}
            >
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                {pipeline.stages.filter((s) => !s.isWon && !s.isLost).map((stage) => (
                  <SelectItem key={stage.id} value={stage.id}>{stage.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Input
            label={`Amount (${pipeline.currency || defaultCurrency})`}
            type="number"
            value={formData.amountCents}
            onChange={(e) => setFormData({ ...formData, amountCents: e.target.value })}
            placeholder="0.00"
            step="0.01"
            min="0"
          />

          <Input
            label="Expected Close Date"
            type="date"
            value={formData.expectedCloseDate}
            onChange={(e) => setFormData({ ...formData, expectedCloseDate: e.target.value })}
          />

          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Source</label>
            <Select
              value={formData.source || NO_SOURCE}
              onValueChange={(v) => setFormData({ ...formData, source: v === NO_SOURCE ? '' : v })}
            >
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_SOURCE}>Select source...</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="web_chat">Web Chat</SelectItem>
                <SelectItem value="phone">Phone</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="referral">Referral</SelectItem>
                <SelectItem value="ad_campaign">Ad Campaign</SelectItem>
                <SelectItem value="walk_in">Walk-in</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Notes</label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="resize-none"
              rows={3}
              placeholder="Additional details..."
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={!formData.name.trim()} isLoading={isSubmitting}>Create Deal</Button>
        </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
